import { translate } from './i18n.js';

const localeLabels = {
    fr: 'Français',
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    ca: 'Català',
    it: 'Italiano',
    ja: '日本語',
    nl: 'Nederlands',
    sv: 'Svenska',
    pl: 'Polski',
    da: 'Dansk'
};

export const locales = {
    createMenu(app, options = {}) {
        const availableLocales = this.getAvailableLocales(app);

        if (availableLocales.length < 2) {
            return null;
        }

        document.getElementById('locales-menu')?.remove();

        const currentLocale = this.getCurrentLocale(app, availableLocales);
        const menu = document.createElement('div');
        const trigger = this.createTrigger(currentLocale);
        const list = this.createList(availableLocales, currentLocale);

        menu.id = 'locales-menu';
        menu.className = 'locales-menu';
        menu.append(trigger, list);

        (options.container || document.body).append(menu);

        this.bindMenu(menu, trigger);

        return menu;
    },

    getAvailableLocales(app) {
        const configuredLocales = app?.config?.lg?.list;

        if (Array.isArray(configuredLocales) && configuredLocales.length > 0) {
            return configuredLocales;
        }

        return Object.keys(localeLabels);
    },

    getCurrentLocale(app, availableLocales) {
        const fallbackLocale = app?.config?.lg?.default || availableLocales[0] || 'fr';
        const documentLocale = document.documentElement.lang || fallbackLocale;

        return availableLocales.includes(documentLocale) ? documentLocale : fallbackLocale;
    },

    createTrigger(currentLocale) {
        const trigger = document.createElement('a');
        trigger.href = this.getLocaleUrl(currentLocale);
        trigger.hreflang = currentLocale;
        trigger.className = 'locales-menu-current ui-buttons square medium no-icon-filter';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', 'locales-menu-list');
        trigger.setAttribute('aria-label', translate('localeMenu.change', currentLocale));
        trigger.append(this.createFlag(currentLocale), this.createCode(currentLocale));

        return trigger;
    },

    createList(availableLocales, currentLocale) {
        const list = document.createElement('div');
        list.id = 'locales-menu-list';
        list.className = 'locales-menu-list';
        list.style.setProperty('--locale-count', availableLocales.length);

        availableLocales.forEach((locale, index) => {
            list.append(this.createItem(locale, currentLocale, index));
        });

        return list;
    },

    createItem(locale, currentLocale, index = 0) {
        const item = document.createElement('a');
        item.href = this.getLocaleUrl(locale);
        item.hreflang = locale;
        item.className = 'locales-menu-item';
        item.dataset.locale = locale;
        item.style.setProperty('--locale-index', index);

        if (locale === currentLocale) {
            item.classList.add('active');
            item.setAttribute('aria-current', 'page');
        }

        item.append(this.createFlag(locale), this.createLabel(locale));

        return item;
    },

    createFlag(locale) {
        const flag = document.createElement('img');
        flag.src = `/images/flag-${locale}.svg`;
        flag.alt = '';
        flag.loading = 'lazy';
        flag.setAttribute('aria-hidden', 'true');

        return flag;
    },

    createCode(locale) {
        const code = document.createElement('span');
        code.textContent = locale.toUpperCase();

        return code;
    },

    createLabel(locale) {
        const label = document.createElement('div');
        label.className = 'label';
        label.textContent = localeLabels[locale] || locale.toUpperCase();

        return label;
    },

    getLocaleUrl(locale) {
        const segments = window.location.pathname.split('/').filter(Boolean);

        if (segments.length > 0 && Object.hasOwn(localeLabels, segments[0])) {
            segments[0] = locale;
        } else {
            segments.unshift(locale);
        }

        return `/${segments.join('/')}${window.location.search}${window.location.hash}`;
    },

    bindMenu(menu, trigger) {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const shouldOpen = !menu.classList.contains('open') || menu.classList.contains('closing');
            this.setMenuOpen(menu, shouldOpen);
        });

        document.addEventListener('click', (event) => {
            if (!menu.contains(event.target)) {
                this.setMenuOpen(menu, false);
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.setMenuOpen(menu, false);
            }
        });
    },

    setMenuOpen(menu, isOpen) {
        menu.querySelector('.locales-menu-current')?.setAttribute('aria-expanded', String(isOpen));
        menu.classList.toggle('open', isOpen);
    }
};
